export type TufRoleHealth = {
  role: "root" | "targets" | "snapshot" | "timestamp";
  version: number;
  expires_at: string;
  remaining_seconds: number;
  status: "healthy" | "expiring" | "expired";
};

export type TufHealth = {
  contract_version: "kavoosh-keys-trust-tuf-health.v1";
  platform: "windows";
  channel: "beta";
  status:
    | "healthy"
    | "expiring"
    | "expired"
    | "invalid"
    | "unavailable"
    | "unconfigured";
  release_blocking: boolean;
  roles: TufRoleHealth[];
  detail: string | null;
};

const API_BASE = import.meta.env.VITE_API_BASE ?? "/api/v1";

const HEALTH_STATES = new Set<TufHealth["status"]>([
  "healthy",
  "expiring",
  "expired",
  "invalid",
  "unavailable",
  "unconfigured",
]);

const ROLE_NAMES = new Set<TufRoleHealth["role"]>([
  "root",
  "targets",
  "snapshot",
  "timestamp",
]);

function assertTufHealth(value: TufHealth): TufHealth {
  if (
    value.contract_version !== "kavoosh-keys-trust-tuf-health.v1"
    || value.platform !== "windows"
    || value.channel !== "beta"
    || !HEALTH_STATES.has(value.status)
    || typeof value.release_blocking !== "boolean"
    || !Array.isArray(value.roles)
  ) {
    throw new Error("TUF health contract is invalid.");
  }
  for (const role of value.roles) {
    if (
      !ROLE_NAMES.has(role.role)
      || !Number.isInteger(role.version)
      || role.version < 1
      || !Number.isFinite(role.remaining_seconds)
      || role.remaining_seconds < 0
      || !["healthy", "expiring", "expired"].includes(role.status)
      || Number.isNaN(Date.parse(role.expires_at))
    ) {
      throw new Error("TUF health role contract is invalid.");
    }
  }
  return value;
}

export async function readTufHealth(): Promise<TufHealth> {
  const response = await fetch(`${API_BASE}/admin/keys-trust/tuf-health`, {
    credentials: "include",
    cache: "no-store",
    headers: { Accept: "application/json" },
  });
  if (response.status === 401) throw new Error("SESSION_UNAVAILABLE");
  if (!response.ok) throw new Error("TUF health could not be loaded.");
  return assertTufHealth(await response.json() as TufHealth);
}
